// As consultas e mutações da tela de plano. A chave `plano` é a que o tempo
// real invalida (comissao_fatura, assinatura_aviso).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api/api';
import { useLoja } from '../../shared/estado/loja';
import type { EstadoDaAssinatura, RegrasDoAnual, ReembolsoDoAnual, StatusDoPlano, VendasDaComissao } from './tipos';

const base = () => `/api/tenant/${encodeURIComponent(useLoja.getState().tenantId ?? '')}`;
export const chaveDoPlano = (tenantId: string) => ['plano', tenantId] as const;

export function useStatusDoPlano() {
  const tenantId = useLoja((s) => s.tenantId) ?? '';
  return useQuery({ queryKey: chaveDoPlano(tenantId), queryFn: () => api.get<StatusDoPlano>(`${base()}/plan/status`), enabled: tenantId !== '' });
}

export function useComissao() {
  const tenantId = useLoja((s) => s.tenantId) ?? '';
  return useQuery({ queryKey: ['plano', tenantId, 'comissao'], queryFn: () => api.get<VendasDaComissao>(`${base()}/comissao/vendas`), enabled: tenantId !== '' });
}

function useRecarregar() {
  const consultas = useQueryClient();
  const tenantId = useLoja((s) => s.tenantId) ?? '';
  return () => consultas.invalidateQueries({ queryKey: chaveDoPlano(tenantId) });
}

export function useDecidirExcedente() {
  const recarregar = useRecarregar();
  return useMutation({ mutationFn: (decision: 'continue' | 'stop') => api.post<{ ok: boolean }>(`${base()}/plan/overage`, { decision }), onSuccess: () => recarregar() });
}

export function useTrocarPlano() {
  const recarregar = useRecarregar();
  return useMutation({ mutationFn: (plan: string) => api.post<{ ok: boolean; billing_synced: boolean }>(`${base()}/plan`, { plan }), onSuccess: () => recarregar() });
}

export function useAgendarCiclo() {
  const recarregar = useRecarregar();
  return useMutation({ mutationFn: (ciclo: 'monthly' | 'yearly') => api.post<{ ok: boolean }>(`${base()}/subscription/ciclo`, { ciclo }), onSuccess: () => recarregar() });
}

/** As regras do anual DO PLANO da loja: sem o `plan`, o servidor responde as do Start. */
export const regrasDoAnual = (plan: string) => api.get<RegrasDoAnual>(`${base()}/subscription/anual/regras?plan=${encodeURIComponent(plan)}`);
export const reembolsoDoAnual = () => api.get<ReembolsoDoAnual>(`${base()}/subscription/anual/reembolso`);

export function useContratarAnual() {
  return useMutation({ mutationFn: (d: { plan: string; parcelas: number }) => api.post<{ ok: boolean; url: string }>(`${base()}/subscription/anual`, { plan: d.plan, aceite: true, parcelas: d.parcelas }) });
}

export function useCancelarAnual() {
  const recarregar = useRecarregar();
  return useMutation({ mutationFn: () => api.post<{ ok: boolean; mensagem: string }>(`${base()}/subscription/anual/cancelar`), onSuccess: () => recarregar() });
}

export const chaveDaAssinatura = (tenantId: string) => ['plano', tenantId, 'assinatura'] as const;
export function useEstadoDaAssinatura() {
  const tenantId = useLoja((s) => s.tenantId) ?? '';
  return useQuery({ queryKey: chaveDaAssinatura(tenantId), queryFn: () => api.get<EstadoDaAssinatura>(`${base()}/subscription`), enabled: tenantId !== '' });
}
/** Cliente no ASAAS, cobrança imediata e recorrência; a resposta traz a página de pagamento. */
export const iniciarAssinatura = (corpo: { plan: string; customer: { name: string; email: string; cpf: string; phone: string } }) => api.post<{ ok: boolean; url?: string }>(`${base()}/subscription/start`, corpo);
/** Confere no ASAAS a cobrança imediata, sem depender do webhook. */
export const verificarPagamento = () => api.post<{ ok: boolean; found?: boolean; paid?: boolean }>(`${base()}/subscription/verify`);
